import type { ResolutionString } from "~/chart";

/**
 * The TradingView charting library only accepts a subset of the Olson
 * timezones that exist. Local copy of the v2 chart constant.
 */
export const UTC_TO_OLSON: Record<string, string> = {
  "-11:00": "Pacific/Niue",
  "-10:00": "Pacific/Honolulu",
  "-09:00": "America/Juneau",
  "-08:00": "America/Los_Angeles",
  "-07:00": "America/Phoenix",
  "-06:00": "America/Mexico_City",
  "-05:00": "America/Bogota",
  "-04:00": "America/Caracas",
  "-03:00": "America/Argentina/Buenos_Aires",
  "-02:00": "Atlantic/South_Georgia",
  "-01:00": "Atlantic/Cape_Verde",
  "+00:00": "Atlantic/Reykjavik",
  "+01:00": "Europe/Berlin",
  "+02:00": "Europe/Athens",
  "+03:00": "Asia/Qatar",
  "+04:00": "Asia/Dubai",
  "+05:00": "Asia/Ashkhabad",
  "+06:00": "Asia/Almaty",
  "+07:00": "Asia/Bangkok",
  "+08:00": "Asia/Hong_Kong",
  "+09:00": "Asia/Tokyo",
  "+10:00": "Australia/Brisbane",
  "+11:00": "Pacific/Norfolk",
  "+12:00": "Pacific/Fiji",
};

/**
 * Poll cadence per resolution. `1D` is deliberately absent: daily bars are
 * not polled, so `subscribeBars` creates no interval for them even though the
 * datafeed advertises `1D` as a supported resolution.
 */
export const TRADING_VIEW_POLLING_INTERVALS: Record<string, number> = {
  "1": 5 * 1000,
  "15": 30 * 1000,
  "30": 30 * 1000,
  "60": 60 * 1000,
};

/** TradingView resolution to market data API timeframe. */
export const TRADING_VIEW_RESOLUTION_TIMEFRAMES: Record<string, string> = {
  "1": "1Min",
  "15": "15Min",
  "30": "30Min",
  "60": "1Hour",
  "1D": "1Day",
};

export const TRADING_VIEW_SUPPORTED_RESOLUTIONS: ResolutionString[] = [
  "1" as ResolutionString,
  "15" as ResolutionString,
  "30" as ResolutionString,
  "60" as ResolutionString,
  "1D" as ResolutionString,
];

export const DAILY_RESOLUTION = "1D";

/** Market data is delayed 15 minutes; one extra minute absorbs clock skew. */
export const DELAYED_MARKET_DATA_MS = 16 * 60_000;
export const MARKET_TIMEZONE = "America/New_York";
export const STOCK_DAILY_HISTORY_DELAY_DAYS = 1;

/**
 * Minimum history window requested from the API so TradingView does not
 * paginate through gaps one tiny window at a time. Overnight stocks need a
 * larger window to clear the 8pm-4am gap in one hop.
 */
export const HISTORY_MIN_WINDOW_MS = 6 * 3_600_000;
export const OVERNIGHT_HISTORY_MIN_WINDOW_MS = 14 * 3_600_000;

/** Consecutive empty history windows tolerated before giving up. */
export const MAX_CONSECUTIVE_EMPTY_WINDOWS = 5;
export const MAX_CONSECUTIVE_EMPTY_WINDOWS_OVERNIGHT = 12;

/** Smallest `nextTime` step back when a window comes back empty. */
export const MIN_EMPTY_WINDOW_STEP_BACK_SEC = 300;

/** Never poll a window shorter than this, regardless of resolution. */
export const MIN_POLL_LOOKBACK_MS = 120_000;

/** Consecutive poll failures tolerated before reporting to Sentry. */
export const MAX_CONSECUTIVE_POLL_ERRORS = 3;

export const HISTORY_BAR_LIMIT = 10_000;

/**
 * Every current v2 `TVChartContainer.defaultProps` value, plus the two
 * defaults v2 applies at the use site rather than in `defaultProps` and ATD's
 * New York chart-time default:
 * `theme` (`this.props.theme ?? "light"`) and `crypto` (undefined, read as
 * `false`). `libraryPath` is relative so it resolves against the deploy base.
 */
export const TRADING_VIEW_DEFAULTS = {
  symbol: "AAPL",
  interval: "15",
  container: "tv_chart_container",
  datafeedUrl: "https://demo_feed.tradingview.com",
  libraryPath: "chart/",
  chartsStorageUrl: "https://saveload.tradingview.com",
  chartsStorageApiVersion: "1.1",
  clientId: "tradingview.com",
  userId: "public_user_id",
  fullscreen: false,
  autosize: true,
  studiesOverrides: {},
  width: "100%",
  baseUrl: "https://data.tradetalk.us/v2",
  seriesStyle: 2,
  utcOffset: "+00:00",
  timezone: MARKET_TIMEZONE,
  exchange: "NYSE",
  isUnlimited: false,
  isOvernightTradable: false,
  crypto: false,
  theme: "light",
} as const;
